import { m } from "framer-motion";
import { FileCheck, Shield, DollarSign, Users, Hammer, MapPin } from "lucide-react";
import { siteConfig } from "@/lib/site-config";

const reasons = [
  {
    icon: FileCheck,
    title: "We Fight Your Insurance Claim",
    desc: "Free damage inspection, full photo documentation, a line-item scope of loss for your carrier, and we meet your adjuster on site. If the first estimate falls short, we file the supplement.",
    accent: "secondary" as const,
  },
  {
    icon: Hammer,
    title: "Every Trade Under One Roof",
    desc: "Roofing, concrete, carpentry, flooring, painting, kitchens and baths — all in-house. No juggling five contractors and no gaps between them.",
    accent: "primary" as const,
  },
  {
    icon: Shield,
    title: "Licensed Texas LLC",
    desc: "R&R House Renovation LLC is a registered Texas company carrying insurance on our work. Documentation available before your project starts.",
    accent: "primary" as const,
  },
  {
    icon: DollarSign,
    title: "Free, Itemized Estimates",
    desc: "Written line-item pricing so you see exactly what you're paying for — with a realistic schedule attached, not an optimistic one.",
    accent: "secondary" as const,
  },
  {
    icon: MapPin,
    title: "Local to DFW — Not Storm Chasers",
    desc: "Based in Aubrey and working Denton, Frisco, Dallas, Fort Worth, Arlington and beyond. We're still here when your warranty matters.",
    accent: "primary" as const,
  },
  {
    icon: Users,
    title: "Bilingual & Owner-Backed",
    desc: `Owner ${siteConfig.owner} stands behind every project personally. Full service in English and Spanish — hablamos español.`,
    accent: "secondary" as const,
  },
];

const WhyChooseUs = () => {
  return (
    <section className="section-padding bg-accent relative overflow-hidden">
      <div className="absolute inset-0 gradient-mesh opacity-30" />

      <div className="container-max relative z-10">
        <div className="text-center mb-14 md:mb-16">
          <m.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-secondary-light text-sm font-bold uppercase tracking-widest mb-3"
          >
            Why R&amp;R House Renovation
          </m.span>
          <m.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-accent-foreground mb-4 tracking-tight font-bold"
          >
            Why Choose <span className="text-gradient-primary">Us</span>
          </m.h2>
          <m.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-accent-foreground/70 max-w-2xl mx-auto text-lg"
          >
            Six reasons DFW homeowners hand us the keys after a storm — and again when it's time to remodel.
          </m.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reasons.map((reason, i) => (
            <m.div
              key={reason.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="glass-card rounded-2xl p-6 md:p-8 card-hover-lift"
            >
              <div className={`inline-flex items-center justify-center h-14 w-14 rounded-2xl mb-5 ${
                reason.accent === "secondary"
                  ? "bg-secondary/25 text-secondary"
                  : "bg-primary/25 text-primary-foreground"
              }`}>
                <reason.icon className="h-7 w-7" />
              </div>
              <h3 className="font-heading text-xl text-accent-foreground mb-2 tracking-tight font-bold">{reason.title}</h3>
              <p className="text-accent-foreground/70 text-sm leading-relaxed">{reason.desc}</p>
            </m.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
